import { createAsyncThunk } from '@reduxjs/toolkit';
import { EDITABLE_FIELDS } from 'components/Campaign/Campaign.schema';
import { differenceInDays } from 'date-fns';
import {
  normalizeDateForApi,
  normalizeDateFromApi,
} from 'helpers/datetimeHelpers';
import { merge, pick } from 'lodash';
import adserver from 'services/adserver';
import { withErrorHandling } from 'store/wrappers';
import { REDUCER_KEY } from './campaigns.consts';
import { selectById, selectCurrentCampaignId } from './campaigns.selectors';

function normalizeApiReponse(campaign) {
  const startDate = normalizeDateFromApi(campaign.startDate);
  const endDate = normalizeDateFromApi(campaign.endDate);

  const totalDurationInDays = differenceInDays(endDate, startDate) || 1;
  const remainingDurationInDays = Math.max(
    0,
    differenceInDays(endDate, new Date()),
  );
  const durationProgressAsPercentage = Math.round(
    ((totalDurationInDays - remainingDurationInDays) / totalDurationInDays) *
      100,
  );

  return {
    ...campaign,
    startDate,
    endDate,
    durationProgressAsPercentage,
    totalDurationInDays,
    remainingDurationInDays,
  };
}

function normalizeApiRequest(data) {
  const editableFieldsOnly = pick(data, Object.keys(EDITABLE_FIELDS));

  if (editableFieldsOnly.startDate || editableFieldsOnly.endDate) {
    const startDate = normalizeDateForApi(editableFieldsOnly.startDate);
    const endDate = normalizeDateForApi(editableFieldsOnly.endDate);

    return {
      ...editableFieldsOnly,
      startDate,
      endDate,
    };
  }
  return editableFieldsOnly;
}

function normalizeBudget(data) {
  return {
    totalBudget: data?.totalBudget,
    spentBudget: data?.spent,
    remainingBudget: data?.remainingBudget,
  };
}

/**
 * wspólny handler dla akcji akcji aktualizujących dane kampanii
 *
 * @param {*} { campaignId }
 * @param {*} thunkApi
 * @return {*} campaignObj
 */
const fetchByIdHandler = async ({ campaignId }) => {
  const { data: campaign } = await adserver({ url: `/campaign/${campaignId}` });

  return {
    ...normalizeApiReponse(campaign),
  };
};

/**
 * wspólny handler dla akcji wykorzystujących metodę api do aktualizacji kampanii
 *
 * @param {*} values
 * @param {*} thunkApi
 * @return {*} campaignObj
 */
const updateHandler = async values => {
  const { id: campaignId } = values;
  if (!campaignId) throw new Error('no id provided');

  const response = await adserver({
    url: '/campaign/' + campaignId,
    method: 'PATCH',
    data: normalizeApiRequest(values),
  });
  return normalizeApiReponse(response.data);
};

export const fetchAll = createAsyncThunk(
  REDUCER_KEY + '/fetchAll',
  withErrorHandling(async () => {
    const response = await adserver({ url: '/campaigns' });
    return response.data.map(campaign => normalizeApiReponse(campaign));
  }),
);

export const fetchById = createAsyncThunk(
  REDUCER_KEY + '/fetchById',
  withErrorHandling(fetchByIdHandler),
);

export const create = createAsyncThunk(
  REDUCER_KEY + '/create',
  withErrorHandling(async values => {
    const response = await adserver({
      url: '/campaign/',
      method: 'POST',
      data: normalizeApiRequest(values),
    });
    return normalizeApiReponse(response.data);
  }),
);

export const fetchCurrentCampaign = createAsyncThunk(
  REDUCER_KEY + '/fetchCurrentCampaign',
  // eslint-disable-next-line no-unused-vars
  withErrorHandling(async (_, thunkAPI) => {
    const campaignId = selectCurrentCampaignId(thunkAPI.getState());
    if (!campaignId)
      return thunkAPI.rejectWithValue('currentCampaignId is unavailable');

    return await fetchByIdHandler({ campaignId });
  }),
);

export const update = createAsyncThunk(
  REDUCER_KEY + '/update',
  withErrorHandling(updateHandler),
);

export const updateStatus = createAsyncThunk(
  REDUCER_KEY + '/updateStatus',
  withErrorHandling(async ({ status, campaignId }) => {
    const { data } = await adserver({
      url: `/campaign/${campaignId}`,
      method: 'PATCH',
      data: { status },
    });
    return normalizeApiReponse(data);
  }),
);

export const toggleStatus = createAsyncThunk(
  REDUCER_KEY + '/toggleStatus',
  withErrorHandling(async (campaignId, thunkAPI) => {
    const campaign = selectById(thunkAPI.getState(), campaignId);
    const isActive = !campaign.isActive;
    await adserver({
      url: `/campaign/${campaignId}/status`,
      method: 'PATCH',
      data: {
        isActive,
      },
    });
    return {
      ...campaign,
      isActive,
    };
  }),
);

export const fetchBudget = createAsyncThunk(
  REDUCER_KEY + '/budget/fetch',
  withErrorHandling(async ({ campaignId }) => {
    const { data } = await adserver({
      url: `/campaign/${campaignId}/budget`,
    });
    return {
      id: campaignId,
      changes: normalizeBudget(data),
    };
  }),
);

export const updateBudget = createAsyncThunk(
  REDUCER_KEY + '/budget/update',
  withErrorHandling(async ({ entitiyId, budgetObj }) => {
    const { data } = await adserver({
      url: `/campaign/${entitiyId}/budget`,
      method: 'PATCH',
      data: budgetObj,
    });
    return {
      id: entitiyId,
      changes: normalizeBudget(data),
    };
  }),
);

export const remove = createAsyncThunk(
  REDUCER_KEY + '/remove',
  withErrorHandling(async ({ campaignId }) => {
    await adserver({
      url: '/campaign/' + campaignId,
      method: 'DELETE',
      headers: {},
    });
    return campaignId;
  }),
);

export const deploy = createAsyncThunk(
  REDUCER_KEY + '/deploy',
  withErrorHandling(async ({ campaignId }, thunkAPI) => {
    await adserver({
      url: `/campaign/${campaignId}/deploy`,
      method: 'POST',
    });

    const campaign = selectById(thunkAPI.getState(), campaignId);
    return merge({}, campaign, {
      bidder: {
        upToDate: true,
      },
    });
  }),
);

export const setCurrent = createAsyncThunk(
  REDUCER_KEY + '/current',
  withErrorHandling(async campaignId => {
    if (!campaignId) return;
    const campaign = await fetchByIdHandler({ campaignId });
    return campaign;
  }),
);

export const removeAllCampaigns = () => ({ type: REDUCER_KEY + '/removeAll' });

export const getReportForCurrentCampaing = createAsyncThunk(
  REDUCER_KEY + '/getReportForCurrentCampaing',
  withErrorHandling(async ({ campaignId }) => {
    const { data } = await adserver({
      url: `/campaign/${campaignId}/report`,
      method: 'GET',
      responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `report_${campaignId}.xlsx`);
    document.body.appendChild(link);
    link.click();
  }),
);

export const updateOwner = createAsyncThunk(
  REDUCER_KEY + '/updateOwner',
  withErrorHandling(async ({ owner, campaignId }) => {
    const { data } = await adserver({
      url: `/campaign/${campaignId}`,
      method: 'PATCH',
      data: { owner },
    });
    return normalizeApiReponse(data);
  }),
);

export const updateCampaignViewTS = createAsyncThunk(
  REDUCER_KEY + '/updateCampaignViewTS',
  withErrorHandling(async ({ campaignId, ts }) => {
    const { data } = await adserver({
      url: `/campaign/${campaignId}/markread`,
      method: 'POST',
      headers: {},
      data: {
        lastViewedTS: ts,
      },
    });

    return data;
  }),
);
